import React, {
 useCallback,
 useEffect,
 useState,
} from 'react'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import {Button} from 'helpers'
import {IItmesConfig} from 'models/GenericForm'
import {
 IOrganisationDocument,
 PAYMENT_TERMS_DOCUMENT_TYPE,
} from 'models/MyAccount'
import {OrganisationDetailsDTO} from 'models/ViewOrganisation'
import {
 createOrganisationDocumentApi,
 getOrganisationDetailsApi,
 getOrganisationDocumentsApi,
 updateOrganisationApi,
 updateOrganisationDocumentApi,
} from 'utils/api/auth/organisationService'
import {updateOnboardingItmes} from 'utils/api/auth/myAccountService'
import {fileUploadHandler} from 'utils/helpers/fileUpload'
import {getFileName, getFileType} from 'utils/helper'
import InfraSection from './InfraSection'
import MandiLicenseSection from './MandiLicenseSection'
import CommodityProfile, {
 mapCommodityProfileToSelected,
 mapSelectedToCommodityProfile,
} from './CommodityProfile'
import PaymentTermsUpload from './PaymentTermsUpload'

type IProps = {
 userId?: string
 organisationId?: number | null
 parentItemConfig?: IItmesConfig
 reloadTabs: () => void
}

const PAYMENT_TERMS_DOCUMENT_TYPES = [
 PAYMENT_TERMS_DOCUMENT_TYPE,
 'PAYMENT_TERMS',
] as const

const isPaymentTermsDocument = (documentType?: string) =>
 Boolean(
  documentType &&
   PAYMENT_TERMS_DOCUMENT_TYPES.includes(
    documentType as (typeof PAYMENT_TERMS_DOCUMENT_TYPES)[number]
   )
 )

const areSameCommoditySet = (a: string[], b: string[]) => {
 if (a.length !== b.length) return false
 const sortedA = [...a].sort()
 const sortedB = [...b].sort()
 return sortedA.every(
  (item, index) => item === sortedB[index]
 )
}

const OfftakerBusinessProfile = (props: IProps) => {
 const {t} = useTranslation()
 const [selectedCommodities, setSelectedCommodities] =
  useState<string[]>([])
 const [initialCommodities, setInitialCommodities] =
  useState<string[]>([])
 const [orgDetails, setOrgDetails] =
  useState<OrganisationDetailsDTO | null>(null)
 const [existingPaymentDoc, setExistingPaymentDoc] =
  useState<IOrganisationDocument | null>(null)
 const [pendingDocumentUrl, setPendingDocumentUrl] =
  useState<string | null>(null)
 const [pendingFilePath, setPendingFilePath] = useState<
  string | null
 >(null)
 const [pendingFileName, setPendingFileName] = useState<
  string | undefined
 >()
 const [pendingFileSize, setPendingFileSize] = useState<
  number | undefined
 >()
 const [uploading, setUploading] = useState(false)
 const [saving, setSaving] = useState(false)
 const [
  existingDocumentMarkedForDeletion,
  setExistingDocumentMarkedForDeletion,
 ] = useState(false)

 const commoditiesDirty = !areSameCommoditySet(
  selectedCommodities,
  initialCommodities
 )
 const paymentTermsDirty = Boolean(
  pendingFilePath || existingDocumentMarkedForDeletion
 )
 const canSave =
  (commoditiesDirty || paymentTermsDirty) &&
  !uploading &&
  !saving

 const loadProfileData = useCallback(async () => {
  if (!props.organisationId) return

  const [details, documents] = await Promise.all([
   getOrganisationDetailsApi(props.organisationId),
   getOrganisationDocumentsApi(props.organisationId),
  ])

  if (details) {
   setOrgDetails(details)
   const selected = mapCommodityProfileToSelected(
    details.commodity_profile
   )
   setSelectedCommodities(selected)
   setInitialCommodities(selected)
  }

  const paymentDoc =
   documents.find((doc) =>
    isPaymentTermsDocument(doc.document_type)
   ) ?? null
  setExistingPaymentDoc(paymentDoc)
  setPendingDocumentUrl(null)
  setPendingFilePath(null)
  setPendingFileName(undefined)
  setPendingFileSize(undefined)
  setExistingDocumentMarkedForDeletion(false)
 }, [props.organisationId])

 useEffect(() => {
  void loadProfileData()
 }, [loadProfileData])

 const handleFileSelected = async (file: File) => {
  const fileType = getFileType(file.type)
  if (!['image', 'document'].includes(fileType)) {
   toast.error('Invalid file type')
   return
  }

  setUploading(true)
  const formData = new FormData()
  formData.append('file', file, getFileName(file.name))
  formData.append('file_type', fileType)
  formData.append('document_type', 'payment_terms')

  const res = await fileUploadHandler(formData)
  setUploading(false)

  if (!res || Array.isArray(res)) {
   toast.error('Failed to upload document')
   return
  }

  if (!res.filepath) {
   toast.error('Failed to upload document')
   return
  }

  setPendingFilePath(res.filepath)
  setPendingDocumentUrl(res.presigned_url || res.filepath)
  setPendingFileName(file.name)
  setPendingFileSize(file.size)
  setExistingDocumentMarkedForDeletion(false)
 }

 const saveCommodityProfile = async () => {
  if (!props.organisationId) return null

  let details = orgDetails
  if (!details) {
   details = await getOrganisationDetailsApi(
    props.organisationId
   )
   if (details) setOrgDetails(details)
  }
  if (!details) return null

  const payload = {
   org_id: props.organisationId,
   commodity_profile: mapSelectedToCommodityProfile(
    selectedCommodities
   ),
  }

  return updateOrganisationApi(payload)
 }

 const savePaymentTermsDocument = async () => {
  if (!props.organisationId) {
   return true
  }

  if (existingPaymentDoc?.id) {
   if (
    !pendingFilePath &&
    !existingDocumentMarkedForDeletion
   ) {
    return true
   }

   const res = await updateOrganisationDocumentApi({
    id: existingPaymentDoc.id,
    document_type: PAYMENT_TERMS_DOCUMENT_TYPE,
    document_url:
     pendingFilePath ||
     (existingDocumentMarkedForDeletion ? 'DELETED' : ''),
    status: 'UPLOADED',
   })
   return Boolean(res)
  }

  if (!pendingFilePath) {
   return true
  }

  const res = await createOrganisationDocumentApi(
   props.organisationId,
   {
    organisation: props.organisationId,
    document_type: PAYMENT_TERMS_DOCUMENT_TYPE,
    document_url: pendingFilePath,
    status: 'UPLOADED',
   }
  )
  return Boolean(res)
 }

 const clearPendingPaymentTermsDocument = () => {
  setPendingDocumentUrl(null)
  setPendingFilePath(null)
  setPendingFileName(undefined)
  setPendingFileSize(undefined)
 }

 const handleDeleteDocument = () => {
  if (pendingFilePath || pendingDocumentUrl) {
   clearPendingPaymentTermsDocument()
   setExistingDocumentMarkedForDeletion(false)
   return
  }

  if (existingPaymentDoc?.id) {
   setExistingDocumentMarkedForDeletion(true)
  }
 }

 const completeOnboardingIfNeeded = async () => {
  const data = props.parentItemConfig
  if (!data || data.status === 'COMPLETED') return

  await updateOnboardingItmes({
   ...(props.userId && {user_id: props.userId}),
   onboarding_items: [
    {
     id: data.id,
     user_category: data?.user_category,
     item_identifier: data?.item_identifier,
     fields: {},
    },
   ],
  })
 }

 const handleSave = async () => {
  if (!canSave || !props.organisationId) {
   if (!props.organisationId) {
    toast.error('Organisation not found')
   }
   return
  }

  setSaving(true)

  const tasks: Promise<unknown>[] = []

  if (commoditiesDirty) {
   tasks.push(saveCommodityProfile())
  }
  if (paymentTermsDirty) {
   tasks.push(savePaymentTermsDocument())
  }

  const results = await Promise.all(tasks)
  const failed = results.some((result) => !result)

  if (failed) {
   setSaving(false)
   toast.error('Failed to save business profile')
   return
  }

  await completeOnboardingIfNeeded()
  await loadProfileData()
  props.reloadTabs()
  setSaving(false)

  if (!commoditiesDirty && paymentTermsDirty) {
   toast.success('Document saved successfully')
  }
 }

 return (
  <div className='w-full'>
   <div className='w-full mb-12'>
    <InfraSection userId={props.userId} />
   </div>

   <div className='w-full mb-12'>
    <MandiLicenseSection userId={props.userId} />
   </div>

   <div className='w-full mb-12'>
    <CommodityProfile
     selectedCommodities={selectedCommodities}
     setSelectedCommodities={setSelectedCommodities}
    />
   </div>

   <div className='w-full mb-12'>
    <PaymentTermsUpload
     existingDocumentUrl={
      existingDocumentMarkedForDeletion
       ? undefined
       : existingPaymentDoc?.document_url
     }
     pendingDocumentUrl={pendingDocumentUrl ?? undefined}
     pendingFileName={pendingFileName}
     pendingFileSize={pendingFileSize}
     onFileSelected={handleFileSelected}
     onDeleteDocument={handleDeleteDocument}
     disabled={uploading || saving}
    />
   </div>

   <div className='w-full'>
    <div className='flex items-center justify-end'>
     <Button
      onClick={handleSave}
      color='blue'
      disabled={!canSave}
      className={`text-center py-[9px] px-6 !rounded-lg text-base font-semibold ${
       canSave
        ? 'bg-[#4E83E4] text-white border-[#4E83E4]'
        : 'bg-[#E6EBF0] text-[#80868C] border-[#E6EBF0] cursor-not-allowed'
      }`}
     >
      {t('myAccountPage.save')}
     </Button>
    </div>
   </div>
  </div>
 )
}

export default OfftakerBusinessProfile
